import { Injectable } from '@angular/core';

import { ApiService } from '@dav-core/services/api.service';
import { FormService } from '@dav-core/services/form.service';
import { DecodeVinResponse } from '@dav-model/api/response/decode-vin.response';
import { ManualResponse } from '@dav-model/api/response/manual.response';
import { UserResponse } from '@dav-model/login/response/user.response.model';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { environment } from 'environments/environment';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';

import * as VehicleActions from './vehicle.actions';
import { State } from './vehicle.reducer';
import * as fromRoot from '../reducers';

@Injectable()
export class VehicleEffects {
  constructor(
    private actions$: Actions,
    private apiService: ApiService,
    private formService: FormService,
    private store: Store<fromRoot.State>
  ) {
  }

  getVehicleForm$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(VehicleActions.GET_PRODUCT_FORM),
      switchMap(() =>
        this.apiService.getProductForm(environment.application).pipe(
          // If successful, dispatch success action with result
          map((response) => {
            return {
              type: VehicleActions.GET_PRODUCT_FORM_SUCCESS,
              payload: response,
            };
          }),
          // If request fails, dispatch failed action
          catchError((error) =>
            of({
              type: VehicleActions.VEHICLE_FAILURE,
              payload: error,
            })
          )
        )
      )
    )
  );

  decodeVin$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(VehicleActions.DECODE_VIN),
      withLatestFrom(this.store.select((state) => state.userState.user)),
      switchMap(([action, user]: [VehicleActions.DecodeVinAction, UserResponse]) => {
        const request = {
          vin: action.payload,
          dealer: user.cmsDealerNumber,
        };

        return this.apiService.decodeVin(request).pipe(
          // If successful, dispatch success action with result
          map((response) => {
            response.loadQuote = false;

            return {
              type: VehicleActions.DECODE_VIN_SUCCESS,
              payload: response,
            };
          }),
          // If request fails, dispatch failed action
          catchError((error) =>
            of({
              type: VehicleActions.DECODE_VIN_FAILURE,
              payload: error,
            })
          )
        );
      })
    )
  );

  vinFailure$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(VehicleActions.DECODE_VIN_FAILURE),
      withLatestFrom(this.store.select((state) => state.vehicleState)),
      switchMap(([, vehicleState]: [VehicleActions.DecodeVinFailureAction, State]) => {
        return this.apiService.manualDecode().pipe(
          // If successful, dispatch success action with result
          map((response: ManualResponse) => {
            const vinManual = new DecodeVinResponse();
            vinManual.year = response.year;

            return { type: VehicleActions.DECODE_VIN_MANUAL, payload: response };
          }),
          // If request fails, dispatch failed action
          catchError((error) => {
            const vinError = new DecodeVinResponse();
            vinError.vin = vehicleState.vin.vin;

            return of({
              type: VehicleActions.VEHICLE_FAILURE,
              payload: error,
            });
          })
        );
      })
    )
  );

  getYear$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(VehicleActions.GET_YEAR),
      withLatestFrom(this.store.select((state) => state.vehicleState.manualDecode)),
      switchMap(([action]: [VehicleActions.GetYearAction, ManualResponse]) =>
        this.apiService.manualDecode(action.payload.category, '-1').pipe(
          map((response: ManualResponse) => ({ type: VehicleActions.GET_YEAR_SUCCESS, payload: response.year })),
          catchError((error) => of({ type: VehicleActions.VEHICLE_FAILURE, payload: error }))
        )
      )
    )
  );

  getMake$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(VehicleActions.GET_MAKE),
      withLatestFrom(this.store.select((state) => state.vehicleState.manualDecode)),
      switchMap(([action]: [VehicleActions.GetMakeAction, ManualResponse]) =>
        this.apiService.manualDecode(action.payload.category, null).pipe(
          map((response: ManualResponse) => ({ type: VehicleActions.GET_MAKE_SUCCESS, payload: response.make })),
          catchError((error) => of({ type: VehicleActions.VEHICLE_FAILURE, payload: error }))
        )
      )
    )
  );

  getModel$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(VehicleActions.GET_MODEL),
      withLatestFrom(this.store.select((state) => state.vehicleState.manualDecode)),
      switchMap(([action]: [VehicleActions.GetModelAction, ManualResponse]) =>
        this.apiService.manualDecode(action.payload.category, action.payload.year, action.payload.make).pipe(
          map((response: ManualResponse) => ({
            type: VehicleActions.GET_MODEL_SUCCESS,
            payload: response.model,
          })),
          catchError((error) => {
            if (error.status === 330) {
              return EMPTY;
            }

            return of({ type: VehicleActions.VEHICLE_FAILURE, payload: error });
          })
        )
      )
    )
  );

  getTrim$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(VehicleActions.GET_TRIM),
      withLatestFrom(this.store.select((state) => state.vehicleState.manualDecode)),
      switchMap(([action, manualResponse]: [VehicleActions.GetTrimAction, ManualResponse]) => {
        const year = action.payload.year;
        const make = action.payload.make;
        const model = action.payload.model;
        const category = action.payload.category;

        return this.apiService.manualDecode(category, year, make, model).pipe(
          map((response: ManualResponse) => {
            manualResponse.trim = response.trim;

            return { type: VehicleActions.GET_TRIM_SUCCESS, payload: manualResponse.trim };
          }),
          // If request fails, dispatch failed action
          catchError((error) => {
            if (error.status === 330) {
              return EMPTY;
            }

            return of({ type: VehicleActions.VEHICLE_FAILURE, payload: error });
          })
        );
      })
    )
  );

  getCustomerPostal$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(VehicleActions.GET_CUSTOMER_POSTAL),
      switchMap((action: VehicleActions.GetCustomerPostalAction) =>
        this.apiService.getPostal({ postalcode: action.payload.zipCode }).pipe(
          map((response: any) => ({ type: VehicleActions.GET_CUSTOMER_POSTAL_SUCCESS, payload: response })),
          catchError(() => of({ type: VehicleActions.VEHICLE_FAILURE, payload: {} }))
        )
      )
    )
  );
}
